import '../operations.css';
import { useState } from 'react';
import { Key, Lock, User } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../api/auth';
import { AiSettings } from './AiSettings';

export function Settings() {
  const { user, setUser } = useAuthStore();
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // Revealed only in-memory after reset; never written to the auth store / localStorage.
  const [revealedApiKey, setRevealedApiKey] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(true);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [apiKeyStatus, setApiKeyStatus] = useState('');

  const forcePasswordChange = Boolean(user?.password_change_required);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.new_password.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await authApi.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
        confirm_password: passwordForm.confirm_password,
      });
      setPasswordSuccess('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      if (user) {
        setUser({ ...user, password_change_required: false });
      }
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setPasswordError(error.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleResetApiKey = () => {
    setShowResetConfirm(true);
    setApiKeyStatus('');
  };

  const handleConfirmReset = async () => {
    setShowResetConfirm(false);
    try {
      const response = await authApi.resetApiKey();
      const newKey = response.data?.data;
      if (typeof newKey === 'string' && newKey !== '') {
        setRevealedApiKey(newKey);
        setShowKey(true);
      }
      setApiKeyStatus('API key reset successfully. Copy it now — it will not be stored in the browser.');
    } catch {
      setApiKeyStatus('Failed to reset API key');
    }
  };

  const handleCancelReset = () => {
    setShowResetConfirm(false);
  };

  const toggleKeyVisibility = () => {
    setShowKey((prev) => !prev);
  };

  return (
    <div className="operations-page max-w-3xl mx-auto space-y-6">
      <div>
        <p className="operations-eyebrow">Workspace preferences</p>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your account settings</p>
      </div>

      {forcePasswordChange && (
        <div role="alert" className="p-3 bg-yellow-50 text-yellow-900 rounded-lg text-sm">
          You must change your password before using AwareNow.
        </div>
      )}

      <Card>
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-indigo-50 rounded-full">
            <User className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Account Information</h3>
            <p className="text-sm text-gray-500">Your account details</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label">Username</label>
            <input type="text" className="input bg-gray-50" value={user?.username ?? ''} disabled />
          </div>
          <div>
            <label className="label">Role</label>
            <input
              type="text"
              className="input bg-gray-50 capitalize"
              value={user?.role?.name ?? ''}
              disabled
            />
          </div>
        </div>
      </Card>

      <Card>
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-yellow-50 rounded-full">
            <Lock className="w-6 h-6 text-yellow-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
            <p className="text-sm text-gray-500">Update your password</p>
          </div>
        </div>

        {passwordError && (
          <div className="p-3 bg-red-50 text-red-700 rounded-lg mb-4 text-sm">
            {passwordError}
          </div>
        )}
        {passwordSuccess && (
          <div className="p-3 bg-green-50 text-green-700 rounded-lg mb-4 text-sm">
            {passwordSuccess}
          </div>
        )}

        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label className="label" htmlFor="current-password">Current Password</label>
            <input
              id="current-password"
              type="password"
              className="input"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="new-password">New Password</label>
            <input
              id="new-password"
              type="password"
              className="input"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="label" htmlFor="confirm-password">Confirm New Password</label>
            <input
              id="confirm-password"
              type="password"
              className="input"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              required
              minLength={8}
            />
          </div>
          <Button type="submit" loading={loading}>
            Update Password
          </Button>
        </form>
      </Card>

      {!forcePasswordChange && (
        <Card>
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-red-50 rounded-full">
              <Key className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">API Key</h3>
              <p className="text-sm text-gray-500">Reset to reveal a new key. Keys are not stored in the browser.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">
                {revealedApiKey ? 'New API Key (copy now)' : 'API Key'}
              </p>
              <code className="text-sm bg-gray-100 px-3 py-1 rounded break-all">
                {revealedApiKey && showKey
                  ? revealedApiKey
                  : revealedApiKey
                    ? '••••••••'
                    : 'Hidden — reset to reveal a new key'}
              </code>
            </div>
            {showResetConfirm ? (
              <div className="flex gap-2">
                <Button variant="danger" size="sm" onClick={handleConfirmReset}>
                  Confirm reset
                </Button>
                <Button variant="secondary" size="sm" onClick={handleCancelReset}>
                  Cancel
                </Button>
              </div>
            ) : revealedApiKey ? (
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={toggleKeyVisibility}>
                  {showKey ? 'Hide key' : 'Show key'}
                </Button>
                <Button variant="danger" onClick={handleResetApiKey}>
                  Reset API Key
                </Button>
              </div>
            ) : (
              <Button variant="danger" onClick={handleResetApiKey}>
                Reset API Key
              </Button>
            )}
          </div>
          {apiKeyStatus && (
            <div role="status" className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
              {apiKeyStatus}
            </div>
          )}
        </Card>
      )}

      {!forcePasswordChange && <AiSettings />}
    </div>
  );
}
